import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaHome, FaRss, FaPlug, FaSignOutAlt, FaCompass, FaSearch, FaEnvelope, FaBroadcastTower, FaChartLine, FaCheckCircle, FaBookmark, FaClock, FaPlusCircle, FaBars, FaTimes, FaPlay, FaShieldAlt, FaBell, FaCoins, FaCrown } from 'react-icons/fa';
import { Swords } from 'lucide-react';
import NotificationsDropdown from './NotificationsDropdown';
import { messagesAPI, arenaAPI } from '../utils/api';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [arenaPending, setArenaPending] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const [currentUser, setCurrentUser] = useState(() => JSON.parse(sessionStorage.getItem('user') || '{}'));

  // Fetch fresh user data on mount (sessionStorage may be stale/empty)
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { authAPI } = await import('../utils/api');
        const res = await authAPI.getMe();
        const user = res.data;
        setCurrentUser(user);
        sessionStorage.setItem('user', JSON.stringify(user));
      } catch { /* session may not exist yet */ }
    };
    if (!currentUser.avatar_url) fetchUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastScrollY && currentY > 60) {
        setNavVisible(false);
      } else {
        setNavVisible(true);
      }
      lastScrollY = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await messagesAPI.getUnreadCount();
      setUnreadMessages(response.data.count || 0);
    } catch {
      setUnreadMessages(0);
    }
  }, []);

  const fetchArenaPending = useCallback(async () => {
    try {
      const response = await arenaAPI.getPendingCount();
      setArenaPending(response.data.total || 0);
    } catch {
      setArenaPending(0);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    fetchArenaPending();
    
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchArenaPending();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchArenaPending]);

  // Reset count when on messages page, then re-fetch actual count
  useEffect(() => {
    if (location.pathname === '/messages') {
      setUnreadMessages(0);
      // Delay re-fetch to allow message-read API calls to complete
      const timer = setTimeout(fetchUnreadCount, 2000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, fetchUnreadCount]);

  const handleLogout = async () => {
    try { const { authAPI } = await import('../utils/api'); await authAPI.logout(); } catch { /* logout best-effort */ }
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const navItems = [
    { icon: FaHome, label: 'Dashboard', path: '/dashboard' },
    { icon: FaRss, label: 'Feed', path: '/feed' },
    { icon: FaCompass, label: 'Explore', path: '/explore' },
    { icon: FaSearch, label: 'Search', path: '/search' },
    { icon: FaEnvelope, label: 'Messages', path: '/messages', badge: unreadMessages },
    { icon: FaBookmark, label: 'Bookmarks', path: '/bookmarks' },
    { icon: FaClock, label: 'Scheduled', path: '/scheduled' },
    { icon: Swords, label: 'PointArena', path: '/arena', badge: arenaPending, arthaus: true },
    { icon: FaBroadcastTower, label: 'PointLive', path: '/go-live', arthaus: true },
    { icon: FaChartLine, label: 'Creators Hub', path: '/creators' },
    { icon: FaCrown, label: 'Creator Studio', path: '/creator' },
    { icon: FaCheckCircle, label: 'Get Verified', path: '/verification' },
    { icon: FaPlay, label: 'PointClips', path: '/clips', arthaus: true },
    { icon: FaCoins, label: 'PointPhoria', path: '/wallet', arthaus: true },
    { icon: FaPlug, label: 'Connections', path: '/connections' },
    { icon: null, label: 'Profile', path: '/profile', isProfile: true },
    { icon: FaShieldAlt, label: 'Settings', path: '/settings' },
  ];

  const mobileNavItems = [
    { icon: FaRss, label: 'Feed', path: '/feed' },
    { icon: FaCompass, label: 'Explore', path: '/explore' },
    { icon: FaEnvelope, label: 'Messages', path: '/messages', badge: unreadMessages },
    { icon: null, label: 'Profile', path: '/profile', isProfile: true },
    { icon: FaBars, label: 'More', action: 'menu' },
  ];

  const ProfileAvatar = React.useCallback(({ className = "w-6 h-6" }) => (
    <img
      src={currentUser.avatar_url || `https://ui-avatars.com/api/?name=${currentUser.full_name || 'U'}&size=40`}
      alt="Profile"
      className={`${className} rounded-full object-cover flex-shrink-0`}
    />
  ), [currentUser.avatar_url, currentUser.full_name]);

  return (
    <>
      {/* Mobile Notification Bell (Dashboard only, top-right) */}
      {location.pathname === '/dashboard' && (
        <div className="fixed top-3 right-4 z-50 md:hidden" data-testid="mobile-notification-bell">
          <NotificationsDropdown />
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="col-span-2 hidden md:block h-screen sticky top-0 border-r border-gray-200 bg-gray-50">
        <div className="p-6 h-full flex flex-col">
          <Link to="/dashboard" className="flex items-center space-x-3 mb-6">
            <img 
              src="/talkinpoint-logo.svg" 
              alt="TalkinPoint" 
              className="h-10 w-auto"
            />
          </Link>

          <div className="flex justify-end mb-4">
            <NotificationsDropdown />
          </div>

          <nav className="space-y-1 flex-1 overflow-y-auto">
            {navItems.map(({ icon: Icon, label, path, highlight, badge, isProfile, arthaus }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center space-x-3 rounded-lg px-4 py-2.5 transition-colors relative ${
                  location.pathname === path
                    ? 'text-[#4A90E2] bg-[#4A90E2]/10 border-l-2 border-l-[#4A90E2]'
                    : highlight
                      ? 'text-pink-500 hover:text-pink-600 hover:bg-pink-50 bg-gradient-to-r from-pink-50 to-purple-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="relative">
                  {isProfile ? (
                    <ProfileAvatar className="w-6 h-6" />
                  ) : (
                    <Icon className={`text-lg ${highlight && location.pathname !== path ? 'text-pink-500' : ''}`} />
                  )}
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1 animate-pulse">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </div>
                <span className={`font-medium text-sm ${arthaus ? 'font-arthaus' : ''}`}>{label}</span>
                {highlight && (
                  <span className="ml-auto bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    NEW
                  </span>
                )}
                {badge > 0 && !highlight && (
                  <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center space-x-3 text-gray-600 hover:text-[#FF0055] hover:bg-gray-100 rounded-lg px-4 py-2.5 transition-colors w-full"
            >
              <FaSignOutAlt className="text-lg" />
              <span className="font-medium text-sm">Logout</span>
            </button>

            <div className="flex items-center justify-center space-x-2 mt-4 pt-4 border-t border-gray-200">
              <span className="text-gray-400 text-xs font-medium tracking-wide">from</span>
              <a href="https://www.gdmhq.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                <img 
                  src="/gdm-logo.png" 
                  alt="GDM" 
                  className="h-4 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className={`fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 safe-area-bottom transition-transform duration-300 ease-in-out ${navVisible ? 'translate-y-0' : 'translate-y-full'}`} data-testid="mobile-nav">
        <div className="flex items-center justify-around px-2 py-1.5">
          {mobileNavItems.map(({ icon: Icon, label, path, badge, action, isProfile }) => (
            action === 'menu' ? (
              <button
                key="menu"
                onClick={() => setMobileMenuOpen(true)}
                className="flex flex-col items-center justify-center py-1 px-3"
                data-testid="mobile-menu-btn"
              >
                <FaBars className="text-lg text-gray-500" />
                <span className="text-[10px] text-gray-500 mt-0.5">More</span>
              </button>
            ) : (
              <Link
                key={path}
                to={path}
                className={`flex flex-col items-center justify-center py-1 px-3 ${
                  location.pathname === path ? 'text-[#4A90E2]' : 'text-gray-500'
                }`}
                data-testid={`mobile-nav-${label.toLowerCase()}`}
              >
                <div className="relative">
                  {isProfile ? (
                    <ProfileAvatar className="w-6 h-6" />
                  ) : (
                    <Icon className="text-lg" />
                  )}
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[8px] font-bold min-w-[14px] h-3.5 flex items-center justify-center rounded-full px-0.5">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] mt-0.5 font-medium">{label}</span>
              </Link>
            )
          ))}
        </div>
      </nav>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <img 
                src="/talkinpoint-logo.svg" 
                alt="TalkinPoint" 
                className="h-8 w-auto"
              />
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-gray-500 hover:text-gray-700">
                <FaTimes size={18} />
              </button>
            </div>
            <nav className="p-3">
              {navItems.map(({ icon: Icon, label, path, highlight, badge, isProfile, arthaus }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 rounded-lg px-4 py-3 transition-colors ${
                    location.pathname === path
                      ? 'text-[#4A90E2] bg-[#4A90E2]/10'
                      : highlight
                        ? 'text-pink-500 bg-gradient-to-r from-pink-50 to-purple-50'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="relative">
                    {isProfile ? (
                      <ProfileAvatar className="w-6 h-6" />
                    ) : (
                      <Icon className="text-lg" />
                    )}
                    {badge > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold w-3.5 h-3.5 flex items-center justify-center rounded-full">
                        {badge > 9 ? '9+' : badge}
                      </span>
                    )}
                  </div>
                  <span className={`font-medium text-sm ${arthaus ? 'font-arthaus' : ''}`}>{label}</span>
                  {highlight && (
                    <span className="ml-auto bg-gradient-to-r from-pink-500 to-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">NEW</span>
                  )}
                </Link>
              ))}
              <button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="flex items-center space-x-3 text-gray-600 hover:text-red-500 hover:bg-gray-100 rounded-lg px-4 py-3 transition-colors w-full mt-2 border-t border-gray-100 pt-4"
              >
                <FaSignOutAlt className="text-lg" />
                <span className="font-medium text-sm">Logout</span>
              </button>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
